import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useFirebasePhoneAuth } from "@/lib/usePhoneAuth";
import { ArrowRight, ArrowLeft, Lightning as Bolt } from "@phosphor-icons/react";

export default function CustomerLogin() {
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("mobile");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const intendedReturn = sessionStorage.getItem("qd_return_to") || "/home";

  const { requestOtp, confirmCode, sending, verifying, resendIn, canResend, error, reset } =
    useFirebasePhoneAuth("recaptcha-customer");

  const sendOtp = async (e) => {
    e?.preventDefault();
    try {
      await requestOtp(mobile);
      setStep("otp");
      toast.success(`OTP sent to +91 ${mobile}`);
    } catch (err) {
      toast.error(err.message || "Failed to send OTP");
    }
  };

  const resend = async () => {
    if (!canResend) return;
    try {
      await requestOtp(mobile);
      toast.success("OTP sent again");
    } catch (err) {
      toast.error(err.message || "Could not resend OTP");
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    try {
      const { idToken } = await confirmCode(otp);
      const r = await api.post("/auth/verify-otp", { id_token: idToken, role: "customer" });
      login(r.data.token, r.data.user, r.data.active_role || "customer");
      toast.success(`Welcome${r.data.user.name ? `, ${r.data.user.name}` : ""}!`);
      sessionStorage.removeItem("qd_return_to");
      reset();
      nav(intendedReturn);
    } catch (err) {
      toast.error(err.message || err.response?.data?.detail || "Invalid OTP");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F9F9]">
      <div id="recaptcha-customer" className="fixed bottom-0 left-0 pointer-events-none" />
      <div className="p-4">
        <Link to="/QDplatform" className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700" data-testid="cust-login-back">
          <ArrowLeft size={18} /> About QuickDukaan
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md fade-up">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#15803D] flex items-center justify-center">
                <Bolt weight="fill" size={22} className="text-white" />
              </div>
              <span className="font-heading text-2xl font-black tracking-tight">QuickDukaan</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">
              {step === "mobile" ? "Login to shop" : "Enter OTP"}
            </h1>
            <p className="text-gray-600 mt-2">
              {step === "mobile" ? "Groceries in 20 minutes" : `Sent to +91 ${mobile}`}
            </p>
          </div>

          <div className="qd-card p-6">
            {step === "mobile" ? (
              <form onSubmit={sendOtp} className="space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Your name <span className="font-normal normal-case text-gray-400">(optional, for new shoppers)</span></label>
                  <input
                    data-testid="cust-name-input"
                    className="qd-input mt-2"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Aman Verma" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Mobile number</label>
                  <div className="mt-2 relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-700 font-semibold text-base pointer-events-none select-none border-r border-gray-300 pr-3">+91</span>
                    <input
                      data-testid="cust-mobile-input"
                      type="tel" inputMode="numeric" autoFocus maxLength={10}
                      className="qd-input pl-[68px] font-mono text-lg"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                      placeholder="9876543210"
                    />
                  </div>
                </div>
                {error && <div className="text-xs text-red-600">{error}</div>}
                <button type="submit" disabled={sending} data-testid="cust-send-otp-btn"
                  className="qd-btn-green w-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {sending ? "Sending…" : "Send OTP"} <ArrowRight weight="bold" size={18} />
                </button>
                <p className="text-xs text-gray-500 text-center">
                  By continuing, you agree to our{" "}
                  <Link to="/terms-and-conditions" className="text-[#15803D] font-semibold">terms</Link>
                  {" "}&{" "}
                  <Link to="/privacy-policy" className="text-[#15803D] font-semibold">privacy policy</Link>.
                </p>
                <div className="pt-4 border-t border-gray-100 text-center">
                  <Link to="/seller/login" className="text-xs font-semibold text-gray-600 hover:text-black" data-testid="cust-go-seller">
                    Are you a seller? <span className="text-[#15803D]">Login here →</span>
                  </Link>
                </div>
              </form>
            ) : (
              <form onSubmit={verifyOtp} className="space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-gray-500">6-Digit OTP</label>
                  <input data-testid="cust-otp-input"
                    type="tel" inputMode="numeric" autoFocus maxLength={6}
                    className="qd-input mt-2 font-mono text-2xl tracking-[0.5em] text-center"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder="••••••" />
                </div>
                {error && <div className="text-xs text-red-600" data-testid="cust-otp-error">{error}</div>}
                <button type="submit" disabled={submitting || verifying} data-testid="cust-verify-otp-btn"
                  className="qd-btn-green w-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {submitting || verifying ? "Verifying…" : "Verify & shop"}
                </button>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <button type="button" className="underline" onClick={() => { reset(); setOtp(""); setStep("mobile"); }} data-testid="cust-change-number">
                    Change number
                  </button>
                  {canResend ? (
                    <button type="button" onClick={resend} disabled={sending}
                      className="text-[#15803D] font-bold" data-testid="cust-resend-otp">
                      Resend OTP
                    </button>
                  ) : (
                    <span data-testid="cust-resend-timer">Resend in {resendIn}s</span>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
